import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, createTheme } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import './index.css';
import App from './App';

// Leica Geosystems brand colors - from official style guide
// Primary: Leica Red (PANTONE 185 C) and Leica Carmine (PANTONE 188 C)
// Greys: Cool Grey scale (PANTONE 1C, 4C, 9C, 11C)
// Dark: PANTONE 433 C

const leicaRed = [
  '#ffedef',
  '#ffd9dd',
  '#f9b0b9',
  '#f38593',
  '#ec5f6f',
  '#e64655',
  '#C41E3A', // Primary - Leica Red
  '#aa1a33',
  '#90152b',
  '#761123',
] as const;

const leicaCarmine = [
  '#f5ebec',
  '#e8d4d6',
  '#d4a8ac',
  '#bf7a80',
  '#a85259',
  '#943640',
  '#7C2128', // PANTONE 188 C
  '#6a1c22',
  '#58171c',
  '#461216',
] as const;

const coolGrey = [
  '#f8f8f7',
  '#D9D9D6', // PANTONE 1 C
  '#BBBCBC', // PANTONE 4 C
  '#a0a1a1',
  '#8A8D8F', // PANTONE 11 C
  '#75787B', // PANTONE 9 C
  '#5e6163',
  '#474a4c',
  '#313436',
  '#1D252D', // PANTONE 433 C (dark)
] as const;

// Special-use colors from style guide
const leicaCyan = [
  '#e6fafa',
  '#ccf5f6',
  '#99ebec',
  '#66e0e3',
  '#40d7db',
  '#2DCCD3', // PANTONE 319 C
  '#26b3b9',
  '#1f999f',
  '#188085',
  '#11666b',
] as const;

const leicaOrange = [
  '#fff3e6',
  '#ffe6cc',
  '#ffcc99',
  '#ffb266',
  '#ff9933',
  '#FF8200', // PANTONE 151 C
  '#e67400',
  '#cc6700',
  '#b35900',
  '#994c00',
] as const;

const theme = createTheme({
  primaryColor: 'leicaRed',
  colors: {
    leicaRed,
    leicaCarmine,
    coolGrey,
    leicaCyan,
    leicaOrange,
  },
  // Typography: Roboto (from style guide)
  fontFamily:
    'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily:
      'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif',
    fontWeight: '500',
  },
  defaultRadius: 'md',
  components: {
    Button: {
      defaultProps: {
        radius: 'md',
      },
    },
    Card: {
      defaultProps: {
        radius: 'lg',
      },
    },
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <DatesProvider settings={{ firstDayOfWeek: 0 }}>
          <ModalsProvider>
            <Notifications position="top-right" />
            <App />
          </ModalsProvider>
        </DatesProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>
);
